// @ts-strict-ignore
import { useTheme } from "@emotion/react";
import { useAviaryTheme } from "@fullscript/aviary-web";
import { faMoon, faSunBright } from "@fullscript/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import type { ThemeTypes } from "@aviary/utils/AviaryThemeContext";
import { useRudderStackAnalytics } from "@shared/components/Rudderstack/InitializeRudderstack/useRudderAnalytics";
import { useIdentify } from "@shared/components/Rudderstack/useIdentify/useIdentify";
import { EMOTION_CACHE_KEY } from "@shared/helpers/createEmotionCache";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useAppcues } from "@shared/hooks/useAppcues/useAppcues";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { Toggle } from "../Toggle";

import * as styles from "./ThemeToggle.styles";

const ThemeToggle = () => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalyticsLogger();

  const { isPatient } = useSharedGlobalConfig();
  const isAppcuesEnabled = useAppcues();
  const analytics = useRudderStackAnalytics(isAppcuesEnabled);
  const identify = useIdentify(isAppcuesEnabled);

  const { setTheme } = useAviaryTheme();
  const currentTheme = useTheme();
  const isDarkMode = currentTheme.theme === "dark";

  const handleToggle = () => {
    logAnalyticsEvent({
      eventName: isPatient
        ? AnalyticsEvent.PATIENT_THEME_DARK_MODE_TOGGLE
        : AnalyticsEvent.STORE_THEME_DARK_MODE_TOGGLE,
      payload: {
        theme_target: isDarkMode ? "light" : "dark",
      },
    });

    let theme: ThemeTypes = "dark";
    if (isDarkMode) {
      theme = "light";
    }
    setTheme(theme);
    // Hack required to remove the global emotion styles after the theme is toggled
    // Without this, the body element's styles aren't updated correctly
    document.querySelectorAll(`[data-emotion="${EMOTION_CACHE_KEY}-global"]`).forEach(el => {
      el.remove();
    });

    if (isAppcuesEnabled && window.preparedUserId) {
      identify(analytics, window.preparedUserId);
    }
  };

  return (
    <Toggle
      isOn={isDarkMode}
      onClick={handleToggle}
      onLabel={t(l.aviary.themeToggle.darkMode)}
      offLabel={t(l.aviary.themeToggle.lightMode)}
      icon={isDarkMode ? faMoon : faSunBright}
      css={styles.label}
    />
  );
};

export { ThemeToggle };
